import React, { useState, useEffect, useRef } from 'react';
import { X, Minus, Maximize2, Minimize2, Send, Building2, User, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { CompanyAvatar } from '@/components/ui/company-avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LiveBadge } from '@/components/ui/live-badge';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { resolveConversationAvatar, getCompanyAvatarProps } from '@/utils/chatAvatars';
import { getChatColorClasses } from '@/utils/chatColors';
import { UserProfileDialog } from '@/components/internal/UserProfileDialog';
import { EnhancedCandidateProfile } from '@/components/hiring/EnhancedCandidateProfile';
import { useTranslation } from 'react-i18next';

interface Message {
  id: string;
  content: string;
  sender_id?: string;
  senderId?: string;
  sender_name?: string;
  senderName?: string;
  sender_avatar_url?: string;
  senderAvatarUrl?: string;
  created_at?: string;
  timestamp?: Date;
  isOwn?: boolean;
  // Enhanced fields for role identification
  sender_user_type?: string;
  sender_participant_role?: string;
}

interface Conversation {
  id: string;
  title?: string;
  name?: string;
  avatar_url?: string;
  avatarUrl?: string;
  resolved_avatar?: string;
  type?: string;
  conversation_category?: string;
  conversationCategory?: string;
  conversation_type?: string;
  unread_count?: number;
  unreadCount?: number;
  user_id?: string;
  userId?: string;
  job_title?: string;
  company_name?: string;
  company_logo_url?: string;
  job_application_id?: string;
  // Application stage info (optional)
  current_stage_title?: string;
  current_stage_status?: 'completed' | 'pending' | 'failed' | string;
  current_stage_id?: string;
  ticket_title?: string;
  ticket_id?: string;
  ticket_number?: string;
  project_name?: string;
  project_id?: string;
  // Profile viewing for direct chats
  other_participant_user_id?: string;
  other_participant_user_type?: string;
}

interface UnifiedChatWindowProps {
  conversation: Conversation;
  messages: Message[];
  isTyping?: boolean;
  typingUsers?: string[] | Array<{ user_name: string }>;
  onSendMessage: (message: string) => void;
  onMinimize: () => void;
  onExpand?: () => void;
  onCollapse?: () => void;
  onClose: () => void;
  isExpanded?: boolean;
  isOnline?: boolean;
  isMyMessage?: (senderId: string) => boolean;
  currentUserId?: string;
  userRole?: 'candidate' | 'hirer';
}

export function UnifiedChatWindow({
  conversation,
  messages,
  isTyping = false,
  typingUsers = [],
  onSendMessage,
  onMinimize,
  onExpand,
  onCollapse,
  onClose,
  isExpanded = false,
  isOnline = false,
  isMyMessage,
  currentUserId,
  userRole
}: UnifiedChatWindowProps) {
  const { t } = useTranslation('ui');
  const [newMessage, setNewMessage] = useState('');
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (timestamp: string | Date) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get chat color classes dynamically
  const chatColors = getChatColorClasses(conversation);

  const isApplicationChat = conversation.conversation_category === 'application' || 
    conversation.conversationCategory === 'application' ||
    !!conversation.job_application_id;

  const isHiringChat = conversation.conversation_category === 'hiring' || 
    conversation.conversationCategory === 'hiring';

  const isSupportChat = conversation.conversation_category === 'support' || 
    conversation.conversationCategory === 'support' ||
    !!conversation.ticket_id;

  const isProjectChat = conversation.conversation_category === 'project' || 
    conversation.conversationCategory === 'project' ||
    !!conversation.project_id;

  const isCandidateView = (userRole === 'candidate' || (conversation as any).isCandidate === true) && (isApplicationChat || isHiringChat);

  // Check if this is a recruiter chat
  const isRecruiterChat = conversation.conversation_category === 'hiring' && 
    (conversation as any).primary_role_context === 'recruiter';

  // Get display title based on conversation type and context
  const getDisplayTitle = () => {
    // Recruiter chats - show company name + job title
    if (isRecruiterChat) {
      if (conversation.company_name && conversation.job_title) {
        return `${conversation.company_name} • ${conversation.job_title}`;
      }
      return conversation.job_title || conversation.company_name || 'Recruiter Chat';
    }
    
    // Application and hiring chats
    if (isApplicationChat || isHiringChat) {
      if (isCandidateView && conversation.job_title) {
        // Candidates see job title only
        return conversation.job_title;
      }
      // Hirers see candidate name (original title/name is usually the candidate name)
      return conversation.title || conversation.name || 'Candidate Chat';
    }
    
    // Support ticket chats
    if (isSupportChat) {
      if (conversation.ticket_title) {
        return conversation.ticket_title;
      }
      if (conversation.ticket_number) {
        return `${t('chat.ticket_prefix')}${conversation.ticket_number}`;
      }
      // Fallback to person's name for support
      return conversation.title || conversation.name || t('chat.support_chat');
    }
    
    // Project chats - show person's name
    if (isProjectChat) {
      return conversation.title || conversation.name || t('chat.project_chat');
    }
    
    // Default: show person's name
    return conversation.title || conversation.name || t('chat.default_chat');
  };

  // Get subtitle based on conversation type and context
  const getSubtitle = () => {
    // Recruiter chats - no subtitle needed since title already shows company + job
    if (isRecruiterChat) {
      return null;
    }
    
    // Application and hiring chats
    if (isApplicationChat || isHiringChat) {
      if (isCandidateView && conversation.company_name) {
        // Candidates see company name as subtitle
        return conversation.company_name;
      }
      if (!isCandidateView && conversation.job_title) {
        // Hirers see job title as subtitle under candidate name
        return conversation.job_title;
      }
    }
    
    // Support ticket chats
    if (isSupportChat) {
      if (conversation.ticket_number && conversation.ticket_title) {
        return `${t('chat.ticket_prefix')}${conversation.ticket_number}`;
      }
      if (conversation.ticket_number) {
        return `${t('chat.ticket_prefix')}${conversation.ticket_number}`;
      }
      return t('chat.support_ticket');
    }
    
    // Project chats
    if (isProjectChat && conversation.project_name) {
      return `${t('chat.project_prefix')}${conversation.project_name}`;
    }
    
    return null;
  };

  return (
    <div 
      className={cn(
        "bg-popover shadow-xl overflow-hidden border transition-colors duration-300",
        "flex flex-col",
        // Mobile: Full screen, Desktop: Sized windows
        isMobile 
          ? "w-full h-full rounded-none" // Mobile: full screen, no rounding
          : isExpanded 
            ? "h-[600px] w-[500px] rounded-3xl" 
            : "h-[400px] w-[350px] rounded-3xl"
      )}
    >
      {/* Header - Mobile Optimized */}
      <div className={cn(
        "flex items-center justify-between shrink-0",
        // Mobile-optimized padding
        isMobile ? "px-4 py-3" : "p-3",
        `${chatColors.background} ${chatColors.foreground}`
      )}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {(() => {
            const companyAvatarProps = getCompanyAvatarProps(conversation);
            if ((conversation as any).primary_role_context === 'talent') {
              return (
                <Avatar className={cn(
                  "ring-2 ring-background/20",
                  isMobile ? "w-10 h-10" : "w-8 h-8"
                )}>
                  <AvatarImage src={(conversation as any).profile_avatar_url || (conversation as any).avatar_url} />
                  <AvatarFallback className={cn(
                    "bg-background/20 text-current",
                    isMobile ? "text-sm" : "text-xs"
                  )}>
                    {getInitials((conversation as any).title || (conversation as any).name || 'Chat')}
                  </AvatarFallback>
                </Avatar>
              );
            }
            if (companyAvatarProps) {
              return (
                <CompanyAvatar 
                  {...companyAvatarProps} 
                  size={isMobile ? "lg" : "md"} 
                  className={cn(
                    "ring-2 ring-background/20",
                    isMobile ? "w-10 h-10" : "w-8 h-8"
                  )} 
                />
              );
            }
            return (
              <Avatar className={cn(
                "ring-2 ring-background/20",
                isMobile ? "w-10 h-10" : "w-8 h-8"
              )}>
                <AvatarImage src={(conversation as any).profile_avatar_url || (conversation as any).avatar_url} />
                <AvatarFallback className={cn(
                  "bg-background/20 text-current",
                  isMobile ? "text-sm" : "text-xs"
                )}>
                  {getInitials((conversation as any).title || (conversation as any).name || 'Chat')}
                </AvatarFallback>
              </Avatar>
            );
          })()}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {conversation.conversation_type === 'direct' && conversation.other_participant_user_id ? (
                <button
                  onClick={() => setShowProfileDialog(true)}
                  className={cn(
                    "font-medium truncate hover:underline transition-colors text-left",
                    isMobile ? "text-base" : "text-sm"
                  )}
                  aria-label={`View ${getDisplayTitle()}'s profile`}
                >
                  {getDisplayTitle()}
                </button>
              ) : (
                <h3 className={cn(
                  "font-medium truncate",
                  isMobile ? "text-base" : "text-sm"
                )}>
                  {getDisplayTitle()}
                </h3>
              )}
              {isOnline && (
                <LiveBadge size={isMobile ? "md" : "sm"} />
              )}
            </div>
            {/* Show subtitle for candidate view or typing indicator */}
            {(() => {
              const subtitle = getSubtitle();
              const hasTyping = typingUsers && typingUsers.length > 0;
              
              if (hasTyping) {
                return (
                  <p className={cn(
                    "opacity-75",
                    isMobile ? "text-sm" : "text-xs"
                  )}>
                    {Array.isArray(typingUsers) ? 
                      (typeof typingUsers[0] === 'string' 
                        ? (typingUsers as string[]).join(', ') 
                        : (typingUsers as Array<{ user_name: string }>).map(u => u.user_name).join(', ')
                      ) : ''} {typingUsers.length === 1 ? t('chat.is_typing') : t('chat.are_typing')}
                  </p>
                );
              }
              
              if (subtitle) {
                return (
                  <p className={cn(
                    "opacity-75 font-medium",
                    isMobile ? "text-sm" : "text-xs"
                  )}>
                    {subtitle}
                  </p>
                );
              }
              
              return null;
            })()}
          </div>
        </div>

        <div className="flex items-center gap-1 ml-2">
          {/* Desktop-only buttons (on mobile, rely on system UI) */}
          {!isMobile && (
            <>
              <Button 
                onClick={onMinimize} 
                variant="ghost" 
                size="sm"
                className="h-7 w-7 p-0 hover:bg-background/20 text-current rounded-full"
              >
                <Minus className="w-3 h-3" />
              </Button>
              <Button 
                onClick={isExpanded ? onCollapse : onExpand} 
                variant="ghost" 
                size="sm"
                className="h-7 w-7 p-0 hover:bg-background/20 text-current rounded-full"
              >
                {isExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
              </Button>
            </>
          )}
          <Button 
            onClick={onClose} 
            variant="ghost" 
            size={isMobile ? "default" : "sm"}
            className={cn(
              "hover:bg-background/20 text-current rounded-full",
              isMobile ? "h-10 w-10 p-0" : "h-7 w-7 p-0"
            )}
          >
            <X className={cn(isMobile ? "w-5 h-5" : "w-3 h-3")} />
          </Button>
        </div>
      </div>

      {/* Application Stage Banner - Below Header (not for recruiter chats) */}
      {isCandidateView && isApplicationChat && !isRecruiterChat && (conversation as any).current_stage_title ? (
        <div className="px-4 py-2 border-b bg-muted/10">
          <div className="flex items-center gap-2">
            <span className={cn(isMobile ? "text-sm" : "text-xs","text-muted-foreground font-medium")}>{t('chat.current_stage')}</span>
            <Badge variant="secondary" className={cn(
              isMobile ? "text-sm" : "text-xs",
              (conversation as any).current_stage_status === 'completed' && "bg-green-500/20 text-green-700 dark:bg-green-500/30 dark:text-green-300 border-green-500/30",
              ((conversation as any).current_stage_status === 'pending' || (conversation as any).current_stage_status === 'in_progress' || !(conversation as any).current_stage_status) && "bg-yellow-500/20 text-yellow-700 dark:bg-yellow-500/30 dark:text-yellow-300 border-yellow-500/30",
              ((conversation as any).current_stage_status === 'failed' || (conversation as any).current_stage_status === 'terminated') && "bg-red-500/20 text-red-700 dark:bg-red-500/30 dark:text-red-300 border-red-500/30"
            )}>
              <Circle className="w-2 h-2 mr-2 fill-current" />
              {(conversation as any).current_stage_title}
            </Badge>
          </div>
        </div>
      ) : null}

      {/* Messages Area - Mobile Optimized */}
      <ScrollArea className="flex-1 p-0">
        <div className={cn(
          "space-y-1",
          isMobile ? "px-1" : ""
        )}>
          {messages.length === 0 ? (
            <div className={cn(
              "flex items-center justify-center text-muted-foreground",
              isMobile ? "h-40" : "h-32"
            )}>
              <p className={cn(
                isMobile ? "text-base" : "text-sm"
              )}>
                {t('chat.no_messages_yet')}
              </p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwnMessage = isMyMessage ? isMyMessage(message.sender_id || message.senderId || '') : 
                (message.sender_id === currentUserId || message.senderId === currentUserId || message.isOwn);
              
              // Get role label for hiring conversations
              const getRoleLabel = () => {
                if (!isHiringChat) return null;
                
                if (message.sender_user_type === 'recruiter' || message.sender_participant_role === 'owner') {
                  return 'Recruiter';
                } else if (message.sender_user_type === 'talent' || message.sender_participant_role === 'recruiter') {
                  return 'Applicant';
                }
                return null;
              };

              const roleLabel = getRoleLabel();
              const isRecruiterMessage = roleLabel === 'Recruiter';
              
              return (
                <div
                  key={message.id}
                  className={cn(
                    "flex gap-2 leading-relaxed",
                    // Mobile-optimized spacing
                    isMobile ? "px-4 py-3 text-sm" : "px-3 py-2 text-xs",
                    isOwnMessage ? "justify-end" : "justify-start"
                  )}
                >
                  {!isOwnMessage && (
                    <div className="flex flex-col items-center gap-1">
                      <Avatar className={cn(
                        "shrink-0",
                        isMobile ? "w-8 h-8" : "w-6 h-6"
                      )}>
                        <AvatarImage src={
                          (conversation as any).profile_avatar_url || 
                          message.sender_avatar_url || 
                          message.senderAvatarUrl || 
                          conversation.avatar_url || 
                          conversation.avatarUrl
                        } />
                        <AvatarFallback className={cn(
                          "bg-muted",
                          isMobile ? "text-xs" : "text-[10px]"
                        )}>
                          {getInitials(message.sender_name || message.senderName || 'U')}
                        </AvatarFallback>
                      </Avatar>
                      {roleLabel && (
                        <div className={cn(
                          "flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium",
                          isRecruiterMessage 
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                            : "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                        )}>
                          {isRecruiterMessage ? (
                            <Building2 className="w-2.5 h-2.5" />
                          ) : (
                            <User className="w-2.5 h-2.5" />
                          )}
                          {roleLabel}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className={cn(
                    "rounded-lg px-3 py-2 break-words",
                    // Mobile-optimized message sizing
                    isMobile ? "max-w-[85%]" : "max-w-[280px]",
                    isOwnMessage 
                      ? "bg-primary text-primary-foreground ml-8" 
                      : "bg-muted text-muted-foreground mr-8"
                  )}>
                    <p className={cn(
                      "leading-relaxed",
                      isMobile ? "text-sm" : "text-xs"
                    )}>
                      {message.content}
                    </p>
                  </div>
                  
                  {isOwnMessage && (
                    <div className="flex flex-col items-center gap-1">
                      <Avatar className={cn(
                        "shrink-0",
                        isMobile ? "w-8 h-8" : "w-6 h-6"
                      )}>
                        <AvatarImage src={message.sender_avatar_url || message.senderAvatarUrl} />
                        <AvatarFallback className={cn(
                          "bg-primary text-primary-foreground",
                          isMobile ? "text-xs" : "text-[10px]"
                        )}>
                          {getInitials(message.sender_name || message.senderName || 'You')}
                        </AvatarFallback>
                      </Avatar>
                      {roleLabel && (
                        <div className={cn(
                          "flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium",
                          isRecruiterMessage 
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                            : "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                        )}>
                          {isRecruiterMessage ? (
                            <Building2 className="w-2.5 h-2.5" />
                          ) : (
                            <User className="w-2.5 h-2.5" />
                          )}
                          {roleLabel}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          
          {isTyping && (
            <div className={cn(
              "flex items-center gap-2",
              isMobile ? "px-4 py-3" : "px-3 py-2"
            )}>
              <Avatar className={cn(isMobile ? "w-8 h-8" : "w-6 h-6")}>
                <AvatarImage src={conversation.avatar_url || conversation.avatarUrl} />
                <AvatarFallback className={cn(
                  "bg-muted",
                  isMobile ? "text-xs" : "text-[10px]"
                )}>
                  {getInitials(conversation.title || conversation.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="bg-muted rounded-lg px-3 py-2">
                <div className="flex gap-1">
                  <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce"></div>
                  <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area - Mobile Optimized */}
      <div className={cn(
        "shrink-0",
        // Mobile-optimized padding
        isMobile ? "px-4 py-3" : "p-3",
        `${chatColors.background} ${chatColors.foreground}`
      )}>
        <div className="flex items-center gap-2">
          
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={t('chat.type_message_placeholder')}
              className={cn(
                "w-full bg-background/20 backdrop-blur-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-current/50 text-current touch-manipulation",
                // Mobile-optimized input sizing (48px minimum)
                isMobile ? "px-4 py-3 text-base min-h-[48px]" : "px-3 py-2 text-xs"
              )}
            />
          </div>
          
          <Button 
            onClick={handleSendMessage}
            variant="ghost"
            size={isMobile ? "default" : "sm"} 
            disabled={!newMessage.trim()}
            className={cn(
              "hover:bg-background/20 text-current rounded-full disabled:opacity-50 touch-manipulation",
              isMobile ? "h-12 w-12 p-0 min-h-[48px]" : "h-8 w-8 p-0"
            )}
          >
            <Send className={cn(isMobile ? "w-5 h-5" : "w-3 h-3")} />
          </Button>
        </div>
      </div>

      {/* Profile Dialog */}
      {conversation.conversation_type === 'direct' && conversation.other_participant_user_id && (
        <>
          {conversation.other_participant_user_type === 'talent' ? (
            <EnhancedCandidateProfile
              candidateId={conversation.other_participant_user_id}
              open={showProfileDialog}
              onOpenChange={setShowProfileDialog}
              nested={true}
            />
          ) : (
            <UserProfileDialog
              userId={conversation.other_participant_user_id}
              open={showProfileDialog}
              onOpenChange={setShowProfileDialog}
              onClose={() => setShowProfileDialog(false)}
              nested={true}
            />
          )}
        </>
      )}
    </div>
  );
}